/**
 * InlineSchedulerBlock — Push-Mode inline visit scheduler.
 *
 * Lets the operator book a service / repair / follow-up / pre-install visit
 * without leaving the current resolve modal. Uses the existing scheduling
 * engine (useScheduling().addEvent) so the booking flows through the normal
 * audit trail and conflict detection.
 *
 * Non-destructive: warns on conflicts but lets the operator proceed.
 */
import { useMemo, useState } from "react";
import { format, parseISO, addDays } from "date-fns";
import { CalendarPlus, AlertTriangle, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useScheduling } from "@/data/scheduling/context";
import {
  EVENT_TYPE_DEPARTMENT,
  EVENT_TYPE_LABELS,
  type EventType,
  type ScheduledEvent,
} from "@/data/scheduling/types";
import type { Job, Issue } from "@/data/types";

type SchedulerKind = "service_call" | "install" | "followup" | "pre_install" | "inspection";

interface Props {
  job: Job;
  issue?: Issue | null;
  /** Fires after the event is created so the parent can log the action into its note. */
  onScheduled?: (evt: ScheduledEvent) => void;
  /** Optional context-aware label for the collapsed trigger button. */
  triggerLabel?: string;
  /** Optional context-aware sub-label / helper text for the collapsed trigger. */
  triggerSubLabel?: string;
  /** When true, render expanded on mount (used by Suggested Move "Adjust"). */
  defaultExpanded?: boolean;
}

/** Decide which visit type makes the most sense based on the issue + job context. */
function suggestKind(issue: Issue | null | undefined, job: Job): SchedulerKind {
  if (!issue) return "service_call";
  const t = issue.issueType ?? "";
  const desc = (issue.description ?? "").toLowerCase();
  const cat = issue.blockerCategory ?? "";

  if (/install|installer|crew/i.test(t) || /reinstall|redo/i.test(desc)) return "install";
  if (/scheduling|reschedul|conflict/i.test(t) || cat === "Scheduling Conflict") return "install";
  if (/inspection|permit/i.test(desc)) return "inspection";
  if (/access|gate|key|lockbox/i.test(desc)) return "pre_install";
  if (/damage|leak|service|repair|warranty/i.test(t + " " + desc)) return "service_call";
  // Default: a follow-up touchpoint
  return job.scheduledInstallDate ? "service_call" : "followup";
}

const KIND_OPTIONS: { value: SchedulerKind; label: string }[] = [
  { value: "service_call", label: "Service / Repair Visit" },
  { value: "install", label: "Install Visit" },
  { value: "pre_install", label: "Pre-Install Check" },
  { value: "inspection", label: "Inspection" },
  { value: "followup", label: "Follow-up Visit" },
];

const TIME_OPTIONS = [
  "08:00", "09:00", "10:00", "11:00",
  "12:00", "13:00", "14:00", "15:00", "16:00",
];

export function InlineSchedulerBlock({ job, issue, onScheduled, triggerLabel, triggerSubLabel, defaultExpanded = false }: Props) {
  const { crews, addEvent, events: allEvents } = useScheduling();
  const activeCrews = useMemo(() => crews.filter((c) => c.active), [crews]);

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [kind, setKind] = useState<SchedulerKind>(() => suggestKind(issue, job));
  const [date, setDate] = useState<Date | undefined>(addDays(new Date(), 1));
  const [startTime, setStartTime] = useState<string>("09:00");
  const [duration, setDuration] = useState<string>("2"); // hours
  const [crewId, setCrewId] = useState<string>(() => activeCrews[0]?.id ?? "");
  const [scheduled, setScheduled] = useState(false);

  // Build candidate ISO start/end strings whenever inputs change.
  const candidate = useMemo(() => {
    if (!date || !crewId) return null;
    const [h, m] = startTime.split(":").map(Number);
    const start = new Date(date);
    start.setHours(h, m ?? 0, 0, 0);
    const end = new Date(start);
    end.setHours(start.getHours() + Number(duration));
    return { startISO: start.toISOString(), endISO: end.toISOString() };
  }, [date, startTime, duration, crewId]);

  // Inline conflict detection — warn but allow.
  const conflict = useMemo(() => {
    if (!candidate || !crewId) return null;
    const newStart = parseISO(candidate.startISO).getTime();
    const newEnd = parseISO(candidate.endISO).getTime();
    const hit = allEvents.find((e) => {
      if (e.crewId !== crewId) return false;
      if (e.status === "cancelled" || e.status === "completed") return false;
      const s = parseISO(e.startTime).getTime();
      const eend = parseISO(e.endTime).getTime();
      return newStart < eend && s < newEnd;
    });
    return hit ?? null;
  }, [candidate, crewId, allEvents]);

  const handleSchedule = () => {
    if (!candidate || !crewId) {
      toast.error("Pick a date, crew, and start time");
      return;
    }
    const crew = crews.find((c) => c.id === crewId);
    if (!crew) return;
    const eventType: EventType = kind;
    const evt: ScheduledEvent = {
      id: `sched-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      jobId: job.id,
      eventType,
      title: `${EVENT_TYPE_LABELS[eventType]} — ${job.customerName}`,
      startTime: candidate.startISO,
      endTime: candidate.endISO,
      crewId: crew.id,
      crewName: crew.name,
      crewType: crew.type,
      status: "scheduled",
      department: EVENT_TYPE_DEPARTMENT[eventType],
      notes: issue ? `Booked from issue ${issue.id}: ${issue.description.slice(0, 120)}` : undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    addEvent(evt);
    setScheduled(true);
    toast.success(
      conflict
        ? `Visit booked despite overlap with ${crew.name}'s existing event`
        : `${EVENT_TYPE_LABELS[eventType]} booked for ${format(parseISO(evt.startTime), "MMM d, h:mm a")}`,
    );
    onScheduled?.(evt);
  };

  // Collapsed entry chip
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-full rounded-md border border-primary/25 bg-primary/[0.04] p-2.5 text-xs flex items-center gap-2 hover:bg-primary/10 transition-colors"
      >
        <CalendarPlus className="h-3.5 w-3.5 text-primary shrink-0" />
        <span className="font-medium text-primary">{triggerLabel ?? "Schedule a visit"}</span>
        <span className="text-muted-foreground flex-1 text-left">
          {triggerSubLabel ?? "Book service, repair, install, or follow-up without leaving this view"}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    );
  }

  if (scheduled) {
    return (
      <div className="rounded-md border border-success/30 bg-success/5 p-3 text-xs flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-success dark:text-success">Visit scheduled</p>
          <p className="text-muted-foreground mt-0.5">
            {date && format(date, "EEE MMM d")} at {startTime} · {crews.find((c) => c.id === crewId)?.name}
          </p>
          <button
            type="button"
            onClick={() => { setScheduled(false); }}
            className="mt-1.5 text-[10px] underline text-muted-foreground hover:text-foreground"
          >
            Book another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-primary/25 bg-primary/[0.03] p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <CalendarPlus className="h-3.5 w-3.5 text-primary" />
          <p className="text-[10px] uppercase tracking-wider font-bold text-primary">Schedule a visit</p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[10px]"
        >
          <ChevronUp className="h-3 w-3" /> Hide
        </button>
      </div>

      {/* Visit type */}
      <div>
        <Label className="text-[10px] text-muted-foreground">Visit type</Label>
        <Select value={kind} onValueChange={(v) => setKind(v as SchedulerKind)}>
          <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {KIND_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date + Time */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn("mt-1 h-8 w-full justify-start text-xs font-normal", !date && "text-muted-foreground")}
              >
                {date ? format(date, "EEE MMM d") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Start time</Label>
          <Select value={startTime} onValueChange={setStartTime}>
            <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIME_OPTIONS.map((t) => (
                <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Duration + Crew */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Duration (hrs)</Label>
          <Input
            type="number"
            min="1"
            max="12"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="mt-1 h-8 text-xs"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Crew / Sub</Label>
          <Select value={crewId} onValueChange={setCrewId}>
            <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select crew" /></SelectTrigger>
            <SelectContent>
              {activeCrews.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-xs">
                  {c.name} {c.type === "subcontractor" ? "(Sub)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Conflict warning — non-blocking */}
      {conflict && (
        <div className="rounded border border-warning/30 bg-warning/5 p-2 text-[11px] flex items-start gap-1.5">
          <AlertTriangle className="h-3 w-3 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-warning dark:text-warning">Crew already booked</p>
            <p className="text-muted-foreground">
              {conflict.title} · {format(parseISO(conflict.startTime), "h:mm a")}–{format(parseISO(conflict.endTime), "h:mm a")}.
              You can still book — operator's call.
            </p>
          </div>
        </div>
      )}

      <Button onClick={handleSchedule} size="sm" className="w-full h-8 text-xs gap-1.5">
        <CalendarPlus className="h-3.5 w-3.5" />
        Book visit
      </Button>
    </div>
  );
}
